import { PageContainer, PageHeader } from '@ccp/ui';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ContactPanel } from '../features/contacts/components/contact-panel.js';
import { TicketsPanel } from '../features/tickets/components/tickets-panel.js';
import { holdsPermission } from '../session/permissions.js';
import { meQueryOptions } from '../session/use-current-user.js';

/**
 * One contact, on a screen of their own.
 *
 * This exists because the command palette's contact search needed somewhere to land. It is
 * deliberately **the same two panels** the agent sees beside a call — the contact with their
 * identities, organisation and history, and their tickets — and not a second implementation of
 * them: somebody looking a person up between calls wants exactly what they would have seen during
 * one.
 *
 * `recentLimit` is larger here than in the call panel. There is room for it, and the question
 * somebody asks when they look a contact up on purpose is usually about a call that is not the
 * last one.
 */
export const Route = createFileRoute('/contacts/$contactId')({
  async beforeLoad({ context }) {
    const me = await context.queryClient.ensureQueryData(meQueryOptions());
    if (!holdsPermission(me, 'contacts.read')) {
      throw redirect({ to: '/', search: { denied: 'contacts.read' } });
    }
  },
  component: ContactScreen,
});

function ContactScreen() {
  const { t } = useTranslation('interactions');
  const { contactId } = Route.useParams();

  return (
    <PageContainer>
      <PageHeader title={t('contact.label')} />
      <div className="grid gap-(--space-lg) lg:grid-cols-2">
        <ContactPanel contactId={contactId} recentLimit={20} />
        <TicketsPanel contactId={contactId} />
      </div>
    </PageContainer>
  );
}
